<template>
    <widget-card
        ref="widgetCardEl"
        :icon="QrCode"
        :info-tooltip="trans('dashboard.v2.asset_status_over_time.info')"
        :loading="loading"
        :title="trans('dashboard.v2.asset_status_over_time.title')"
    >
        <template v-if="lastUpdated" #header-subtitle>
            <div class="small mt-1 text-muted">{{ trans('dashboard.v2.last_update') }}: {{ lastUpdated }}</div>
        </template>
        <template #header-end>
            <div class="d-flex align-items-center gap-2">
                <jet-button v-if="showDebug" class="btn-icon" variant="secondary" @click="showDebugInfo = !showDebugInfo">
                    <icon name="cil-bug" />
                </jet-button>
            </div>
        </template>

        <div class="h-100">
            <chart-loading-skeleton v-if="loading" />
            <empty-widget-state v-else-if="!hasData" :text="trans('dashboard.v2.asset_status_over_time.empty_state')" />
            <div v-else class="chart-wrapper" :style="{ minHeight: chartHeight + 'px' }">
                <div class="chart-container">
                    <c-chart
                        :key="componentKey"
                        ref="chartRef"
                        :data="chartData"
                        :custom-tooltips="false"
                        :options="chartOptions"
                        :plugins="chartPlugins"
                        type="bar"
                    />
                </div>

                <div class="legend-container">
                    <div class="legend-item">
                        <span class="legend-dot operable"></span>
                        <span class="legend-text">{{ trans('dashboard.v2.asset_status_over_time.legend.operable') }}</span>
                    </div>
                    <div class="legend-item">
                        <span class="legend-dot inoperable"></span>
                        <span class="legend-text">{{ trans('dashboard.v2.asset_status_over_time.legend.inoperable') }}</span>
                    </div>
                    <div class="legend-item">
                        <span class="legend-dot current-time"></span>
                        <span class="legend-text">{{ trans('dashboard.v2.asset_status_over_time.legend.current_time') }}</span>
                    </div>
                </div>
            </div>
        </div>
    </widget-card>
    <debug-view v-if="showDebug" v-model="showDebugInfo" title="Asset Status Over Time<br/><small>Debug Info</small>">
        <pre style="background-color: #fefefe">{{ debugInfo }}</pre>
    </debug-view>
</template>

<script setup>
import { usePage } from '@inertiajs/vue3';
import { computed, inject, ref, toRef, watch } from 'vue';
import WidgetCard from '@/Pages/DashboardV2/Components/WidgetCard.vue';
import ChartLoadingSkeleton from '@/Pages/DashboardV2/Components/ChartLoadingSkeleton.vue';
import EmptyWidgetState from '@/Pages/DashboardV2/Components/EmptyWidgetState.vue';
import { CChart } from '@coreui/vue-chartjs';
import QrCode from '../../../../icons/QrCode.vue';
import { trans } from '@/mixins/i18n';
import DebugView from '@/Components/DebugView.vue';
import JetButton from '@/Jetstream/Button.vue';
import Icon from '@/Components/Icon.vue';
import { useChartWidget } from '@/composables/useChartWidget';
import { withTouchStickyTooltip } from '@/Utils/chartDefaults';
import { escapeHtml } from '@/Utils/escapeHtml';

// ============================================================================
// Constants
// ============================================================================

const COLORS = {
    OPERABLE: '#1d4ed8',
    INOPERABLE: '#b91c1c',
    INOPERABLE_LABEL_BG: '#fecaca',
    INOPERABLE_LABEL_TEXT: '#991b1b',
    CURRENT_TIME: '#10b981',
    Y_AXIS_SUBLABEL: '#5577F5',
    Y_AXIS_NAME: '#1f2937',
    GRID: '#f1f5f9',
    TICK: '#8490a1',
};

const BAR_THICKNESS = {
    OPERABLE: 10,
    INOPERABLE: 14,
};

const CHART_CONFIG = {
    HEIGHT_PER_ASSET: 60,
    MIN_HEIGHT: 400,
    PADDING_LEFT: 200,
    GRID_TICK_COUNT: 15,
};

const GRID_CELL_SIZE = 83; // GridStack cellHeight; each h-unit adds 83px to item height
const GRID_WIDGET_OVERHEAD = 142; // item margin insets (20) + widget-card-header (~75px) + body padding (32px) + buffer (15px)
const GRID_MIN_H = 5;

const DATETIME_FORMAT = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
};

// ============================================================================
// Props & Component Setup
// ============================================================================

const props = defineProps({
    widgetData: {
        type: Object,
        required: true,
        default: () => ({}),
    },
    filters: {
        type: Object,
        default: () => ({}),
    },
});

const chartRef = ref(null);
const widgetCardEl = ref(null);
const resizeWidget = inject('resizeWidget', null);

const {
    data,
    loading,
    componentKey,
    lastUpdated,
    debug: showDebug,
    showDebugInfo,
    debugInfo,
} = useChartWidget({
    dataUrl: computed(() => props.widgetData?.dataUrl),
    filters: toRef(props, 'filters'),
    parseResponse: (response) => ({
        data: {
            assets: response.data.data.assets || [],
            meta: response.data.data.meta || {},
        },
        lastUpdated: response.data.data.lastUpdated,
        debug: response.data.data.debug,
    }),
});

// ============================================================================
// Utility Functions
// ============================================================================

function parseTimestampToSeconds(timestamp, startDate) {
    const date = new Date(timestamp);
    return (date - startDate) / 1000;
}

function formatDuration(seconds) {
    const totalMinutes = Math.round(seconds / 60);

    if (totalMinutes < 1) return '< 1m';

    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const minutes = totalMinutes % 60;

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);

    return parts.join(' ');
}

function formatDateTimeInZone(date, timezone) {
    const locale = usePage().props.locale || 'en';
    return date.toLocaleString(locale, { ...DATETIME_FORMAT, timeZone: timezone });
}

function computeNowSecondsForAsset(windowStart, windowEnd) {
    if (!windowStart || !windowEnd) return null;
    const now = Date.now();
    if (now < windowStart.getTime() || now > windowEnd.getTime()) return null;
    return Math.max(0, Math.min((now - windowStart) / 1000, (windowEnd - windowStart) / 1000));
}

function buildPeriodsFromStatusChanges(statusChanges, startDate, endDate, nowSeconds) {
    const operablePeriods = [];
    const inoperablePeriods = [];
    const inoperableLabels = [];

    if (statusChanges.length === 0 || !startDate || !endDate) {
        return { operablePeriods, inoperablePeriods, inoperableLabels };
    }

    const totalSeconds = (endDate - startDate) / 1000;
    const clipAt = typeof nowSeconds === 'number' ? Math.min(nowSeconds, totalSeconds) : totalSeconds;

    for (let i = 0; i < statusChanges.length; i++) {
        const current = statusChanges[i];
        const next = statusChanges[i + 1];

        const currentTime = parseTimestampToSeconds(current.timestamp, startDate);
        const nextTime = next ? parseTimestampToSeconds(next.timestamp, startDate) : totalSeconds;
        const periodEnd = Math.min(nextTime, clipAt);

        if (periodEnd <= currentTime) {
            continue;
        }

        if (current.status === 'operable') {
            operablePeriods.push([currentTime, periodEnd]);
        } else if (current.status === 'inoperable') {
            inoperablePeriods.push([currentTime, periodEnd]);
            const durationSeconds = periodEnd - currentTime;
            inoperableLabels.push(formatDuration(durationSeconds));
        }
    }

    return { operablePeriods, inoperablePeriods, inoperableLabels };
}

function createColorBox(color) {
    return `<span style="display:inline-block;width:12px;height:12px;background-color:${color};margin-right:6px;border-radius:2px;"></span>`;
}

function buildTooltipHtml(asset, rawAsset) {
    let html = '<thead>';
    html += `<tr><th>${escapeHtml(asset.modelDesignation)} - ${escapeHtml(asset.locationName)}</th></tr>`;
    html += '</thead><tbody>';

    if (rawAsset?.statusChanges) {
        const timezone = asset.timezone ?? 'UTC';
        const endDate = asset.windowEnd ?? new Date();

        rawAsset.statusChanges.forEach((change, i) => {
            const next = rawAsset.statusChanges[i + 1];
            const startDate = new Date(change.timestamp);
            const endDateForPeriod = next ? new Date(next.timestamp) : endDate;
            const durationSeconds = (endDateForPeriod - startDate) / 1000;

            const statusLabel =
                change.status === 'operable'
                    ? trans('dashboard.v2.asset_status_over_time.legend.operable')
                    : trans('dashboard.v2.asset_status_over_time.legend.inoperable');
            const color = change.status === 'operable' ? COLORS.OPERABLE : COLORS.INOPERABLE;

            const row = [
                createColorBox(color),
                escapeHtml(statusLabel),
                ': ',
                escapeHtml(formatDateTimeInZone(startDate, timezone)),
                ' - ',
                escapeHtml(formatDateTimeInZone(endDateForPeriod, timezone)),
                ' (',
                escapeHtml(formatDuration(durationSeconds)),
                ')',
            ].join('');

            html += `<tr><td>${row}</td></tr>`;
        });
    }

    html += '</tbody>';
    return html;
}

// ============================================================================
// Data Transformation
// ============================================================================

const transformedData = computed(() => {
    const assets = data.value?.assets || [];
    const meta = data.value?.meta || {};
    const fallbackStart = meta.start ? new Date(meta.start + 'T00:00:00Z') : null;
    const endOfMetaDay = meta.end ? new Date(meta.end + 'T23:59:59Z') : null;
    const fallbackEnd = endOfMetaDay ? new Date(Math.min(Date.now(), endOfMetaDay.getTime())) : null;

    return assets.map((asset) => {
        const windowStart = asset.windowStart ? new Date(asset.windowStart) : fallbackStart;
        const windowEnd = asset.windowEnd ? new Date(asset.windowEnd) : fallbackEnd;
        const timezone = asset.timezone ?? 'UTC';
        const nowSeconds = computeNowSecondsForAsset(windowStart, windowEnd);

        const { operablePeriods, inoperablePeriods, inoperableLabels } = buildPeriodsFromStatusChanges(
            asset.statusChanges || [],
            windowStart,
            windowEnd,
            nowSeconds,
        );

        return {
            modelDesignation: asset.modelDesignation,
            locationName: asset.locationName,
            timezone,
            windowStart,
            windowEnd,
            nowSeconds,
            operablePeriods,
            inoperablePeriods,
            inoperableLabels,
        };
    });
});

const hasData = computed(() => transformedData.value.length > 0);

const chartHeight = computed(() => {
    const assetCount = transformedData.value.length;
    return Math.max(CHART_CONFIG.MIN_HEIGHT, assetCount * CHART_CONFIG.HEIGHT_PER_ASSET);
});

watch(
    chartHeight,
    (newChartHeight) => {
        if (!hasData.value) return;

        const gsItem = widgetCardEl.value?.$el?.closest('.grid-stack-item');
        if (!gsItem || !resizeWidget) return;

        const neededPx = newChartHeight + GRID_WIDGET_OVERHEAD;
        const maxPx = window.innerHeight * 0.85;
        const targetH = Math.max(GRID_MIN_H, Math.min(Math.ceil(neededPx / GRID_CELL_SIZE), Math.floor(maxPx / GRID_CELL_SIZE)));

        resizeWidget(gsItem, targetH);
    },
    { immediate: true },
);

const labels = computed(() => transformedData.value.map((item) => [item.modelDesignation, item.locationName]));

const timeRange = computed(() => {
    const meta = data.value?.meta || {};
    const startDate = meta.start ? new Date(meta.start + 'T00:00:00Z') : null;
    const endDate = meta.end ? new Date(meta.end + 'T23:59:59Z') : null;
    const totalSeconds = startDate && endDate ? (endDate - startDate) / 1000 : 30 * 24 * 60 * 60;
    const totalDays = totalSeconds / 86400;

    return { startDate, endDate, totalSeconds, totalDays };
});

// ============================================================================
// Chart Data & Datasets
// ============================================================================

const chartData = computed(() => {
    if (!hasData.value) {
        return { labels: [], datasets: [] };
    }

    const chartAssets = transformedData.value;

    const createDatasets = (type, maxCount) => {
        const datasets = [];
        const isOperable = type === 'operable';
        const label = trans(`dashboard.v2.asset_status_over_time.legend.${type}`);

        for (let i = 0; i < maxCount; i++) {
            datasets.push({
                label,
                data: chartAssets.map((item) => {
                    const periods = isOperable ? item.operablePeriods : item.inoperablePeriods;
                    return periods[i] ? [periods[i][0], periods[i][1]] : null;
                }),
                backgroundColor: isOperable ? COLORS.OPERABLE : COLORS.INOPERABLE,
                borderRadius: 0,
                borderSkipped: false,
                barThickness: isOperable ? BAR_THICKNESS.OPERABLE : BAR_THICKNESS.INOPERABLE,
                grouped: false,
                order: isOperable ? 2 : 1,
                ...(isOperable ? {} : { durationLabels: chartAssets.map((item) => item.inoperableLabels[i] || null) }),
            });
        }

        return datasets;
    };

    const maxOperablePeriods = Math.max(...chartAssets.map((d) => d.operablePeriods.length), 0);
    const maxInoperablePeriods = Math.max(...chartAssets.map((d) => d.inoperablePeriods.length), 0);

    return {
        labels: labels.value,
        datasets: [...createDatasets('operable', maxOperablePeriods), ...createDatasets('inoperable', maxInoperablePeriods)],
    };
});

// ============================================================================
// Chart Plugins
// ============================================================================

const customYAxisPlugin = computed(() => ({
    id: 'customYAxis',
    afterDatasetsDraw(chart) {
        const { ctx, scales } = chart;
        const yScale = scales.y;
        if (!yScale?.ticks) return;

        ctx.save();
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';

        const chartLabels = labels.value;

        yScale.ticks.forEach((tick, index) => {
            const labelData = chartLabels[index];
            if (!labelData || !Array.isArray(labelData) || labelData.length < 2) return;

            const y = yScale.getPixelForTick(index);
            const x = 8;

            ctx.font = 'bold 11px sans-serif';
            ctx.fillStyle = COLORS.Y_AXIS_SUBLABEL;
            ctx.fillText(labelData[0], x, y - 8);

            ctx.font = '400 11px sans-serif';
            ctx.fillStyle = COLORS.Y_AXIS_NAME;
            ctx.fillText(labelData[1], x, y + 6);
        });

        ctx.restore();
    },
}));

const currentTimePlugin = computed(() => ({
    id: 'currentTimeLine',
    afterDraw(chart) {
        const { ctx, chartArea, scales } = chart;
        const yScale = scales.y;
        const xScale = scales.x;

        if (!yScale?.ticks) return;

        const assets = transformedData.value;
        const tickCount = yScale.ticks.length;
        let firstDrawnXPos = null;

        ctx.save();

        assets.forEach((asset, index) => {
            if (asset.nowSeconds === null) return;

            const xPos = xScale.getPixelForValue(asset.nowSeconds);
            const yCenter = yScale.getPixelForTick(index);

            const prevY = index > 0 ? yScale.getPixelForTick(index - 1) : chartArea.top;
            const nextY = index < tickCount - 1 ? yScale.getPixelForTick(index + 1) : chartArea.bottom;
            const yTop = index > 0 ? (yCenter + prevY) / 2 : chartArea.top;
            const yBottom = index < tickCount - 1 ? (yCenter + nextY) / 2 : chartArea.bottom;

            ctx.setLineDash([4, 4]);
            ctx.strokeStyle = COLORS.CURRENT_TIME;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(xPos, yTop);
            ctx.lineTo(xPos, yBottom);
            ctx.stroke();

            if (firstDrawnXPos === null) {
                firstDrawnXPos = xPos;
            }
        });

        if (firstDrawnXPos !== null) {
            ctx.setLineDash([]);
            ctx.fillStyle = COLORS.CURRENT_TIME;
            ctx.beginPath();
            ctx.arc(firstDrawnXPos, chartArea.top - 5, 5, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.restore();
    },
}));

const durationLabelPlugin = {
    id: 'durationLabels',
    afterDatasetsDraw(chart) {
        const { ctx, scales } = chart;
        const xScale = scales.x;

        ctx.save();

        chart.data.datasets.forEach((dataset, datasetIndex) => {
            if (dataset.label !== trans('dashboard.v2.asset_status_over_time.legend.inoperable')) return;
            if (!dataset.durationLabels) return;

            const meta = chart.getDatasetMeta(datasetIndex);
            meta.data.forEach((element, index) => {
                const label = dataset.durationLabels[index];
                if (!label || element.hidden) return;

                // Get the raw data point [start, end] from the dataset
                const dataPoint = dataset.data[index];
                if (!dataPoint || !Array.isArray(dataPoint)) return;

                const [start, end] = dataPoint;
                const centerValue = (start + end) / 2;
                const centerX = xScale.getPixelForValue(centerValue);

                ctx.font = 'bold 10px sans-serif';
                const labelWidth = ctx.measureText(label).width + 10;

                ctx.fillStyle = COLORS.INOPERABLE_LABEL_BG;
                ctx.beginPath();
                ctx.roundRect(centerX - labelWidth / 2, element.y - 28, labelWidth, 16, 4);
                ctx.fill();

                ctx.fillStyle = COLORS.INOPERABLE_LABEL_TEXT;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(label, centerX, element.y - 20);
            });
        });

        ctx.restore();
    },
};

const chartPlugins = computed(() => [customYAxisPlugin.value, currentTimePlugin.value, durationLabelPlugin]);

// ============================================================================
// Chart Options & Tooltip
// ============================================================================

function createTooltipHandler(context) {
    let tooltipEl = document.getElementById('chartjs-tooltip');

    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.id = 'chartjs-tooltip';
        tooltipEl.innerHTML = '<table></table>';
        document.body.appendChild(tooltipEl);
    }

    const tooltipModel = context.tooltip;
    if (tooltipModel.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
    }

    tooltipEl.classList.remove('above', 'below', 'no-transform');
    if (tooltipModel.yAlign) {
        tooltipEl.classList.add(tooltipModel.yAlign);
    } else {
        tooltipEl.classList.add('no-transform');
    }

    const dataPoints = tooltipModel.dataPoints || [];
    if (dataPoints.length === 0) {
        tooltipEl.style.opacity = 0;
        return;
    }

    const assetIndex = dataPoints[0].dataIndex;
    const asset = transformedData.value[assetIndex];
    const rawAsset = (data.value?.assets || [])[assetIndex];

    const tableRoot = tooltipEl.querySelector('table');
    tableRoot.innerHTML = buildTooltipHtml(asset, rawAsset);

    const position = context.chart.canvas.getBoundingClientRect();
    const chartArea = context.chart.chartArea;
    const centerX = (chartArea.left + chartArea.right) / 2;

    tooltipEl.style.opacity = 1;
    tooltipEl.style.position = 'fixed';
    tooltipEl.style.padding = tooltipModel.padding + 'px ' + tooltipModel.padding + 'px';
    tooltipEl.style.pointerEvents = 'none';

    const tooltipWidth = tooltipEl.offsetWidth;
    const idealLeft = position.left + centerX - tooltipWidth / 2;
    const clampedLeft = Math.max(8, Math.min(idealLeft, window.innerWidth - tooltipWidth - 8));
    tooltipEl.style.left = clampedLeft + 'px';
    tooltipEl.style.top = position.top + tooltipModel.caretY + 'px';
}

const chartOptions = computed(() => {
    const { startDate, totalSeconds, totalDays } = timeRange.value;

    return withTouchStickyTooltip({
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        grouped: false,
        layout: {
            padding: {
                bottom: 20,
                top: 20,
                left: CHART_CONFIG.PADDING_LEFT,
                right: 0,
            },
        },
        scales: {
            x: {
                type: 'linear',
                position: 'top',
                min: 0,
                max: totalSeconds,
                grid: {
                    display: true,
                    color: COLORS.GRID,
                    borderDash: [2, 2],
                },
                ticks: {
                    stepSize: Math.max(1, Math.floor(totalDays / CHART_CONFIG.GRID_TICK_COUNT)) * 24 * 60 * 60,
                    callback: function (value) {
                        if (!startDate) return value;
                        const date = new Date(startDate);
                        date.setTime(date.getTime() + value * 1000);
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    },
                    color: COLORS.TICK,
                    font: { size: 11 },
                },
                border: { display: false },
            },
            y: {
                grid: { display: false },
                ticks: { display: false },
                border: { display: false },
                offset: true,
            },
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: false,
                external: createTooltipHandler,
            },
        },
    });
});
</script>

<style scoped>
.chart-wrapper {
    display: flex;
    gap: 1.5rem;
    height: 100%;
}

.chart-container {
    flex: 1;
    min-width: 0;
    position: relative;
}

/* LEGEND */

.legend-container {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 0.75rem;
    padding: 1rem;
    min-width: 140px;
    position: sticky;
    top: 0;
    align-self: flex-start;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    flex-shrink: 0;
}

.legend-dot.operable {
    background-color: #1d4ed8;
}

.legend-dot.inoperable {
    background-color: #b91c1c;
}

.legend-dot.current-time {
    background-color: #10b981;
    border-radius: 50%;
}

.legend-text {
    font-size: 0.75rem;
    color: #666;
    white-space: nowrap;
}

/* Match UseMedia isMobile (max-width: 575px) */
@media (max-width: 575px) {
    .legend-container {
        display: none;
    }
}
</style>

<style>
/* Unscoped styles for external tooltip (appended to body) */
#chartjs-tooltip {
    position: fixed;
    max-width: calc(100vw - 16px);
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    color: #333;
    padding: 8px 12px;
    font-size: 12px;
    line-height: 1.5;
    pointer-events: none;
}

#chartjs-tooltip table {
    margin: 0;
    border-spacing: 0;
    border-collapse: collapse;
}

#chartjs-tooltip thead th {
    font-weight: 600;
    padding: 0 0 8px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    color: #1f2937;
    text-align: left;
}

#chartjs-tooltip tbody td {
    padding: 4px 0;
    white-space: normal;
    color: #333;
}
</style>
